/**
 * QuestionCarousel Component
 * Visar 5 obesvarade frågor från olika livsteman
 * Användaren kan klicka på en fråga för att börja svara
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import lifeThemeAPI from '../../services/lifeThemeAPI';

const QuestionCarousel = ({ onHelpClick }) => {
  const [questions, setQuestions] = useState([]);
  const [totalUnanswered, setTotalUnanswered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCarousel();
  }, []);

  const loadCarousel = async () => {
    try {
      setLoading(true);
      const data = await lifeThemeAPI.getQuestionCarousel();
      setQuestions(data.questions);
      setTotalUnanswered(data.total_unanswered);
    } catch (err) {
      console.error('Error loading question carousel:', err);
      setError('Kunde inte ladda frågor. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionClick = (question) => {
    // Navigera till frågedetalj-sidan för temat
    navigate(`/client/life-themes/${question.theme_id}/question/${question.question_id}`);
  };

  const handleRefresh = () => {
    loadCarousel();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadCarousel}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Försök igen
        </button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <svg
          className="mx-auto h-16 w-16 text-green-500 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="text-xl font-semibold text-green-900 mb-2">
          Grattis! Du har besvarat alla frågor
        </h3>
        <p className="text-green-700 mb-6">
          Gå till översikten för att se dina resultat.
        </p>
        <button
          onClick={() => navigate('/client/life-themes/overview')}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
        >
          Se resultat
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Välj ett påstående
            </h2>
          </div>
          <div className="flex gap-2">
            {onHelpClick && (
              <button
                onClick={onHelpClick}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Hjälp
              </button>
            )}
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Visa andra frågor
            </button>
          </div>
        </div>

        {/* Question Cards */}
        <div className="grid gap-4">
        {questions.map((question, index) => (
          <button
            key={question.question_id}
            onClick={() => handleQuestionClick(question)}
            className="group text-left bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-brand-primary hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-start items-center gap-4">
              {/* Number Badge */}
              <div className="flex-shrink-0 w-10 h-10 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center font-bold text-lg group-hover:bg-brand-primary group-hover:text-white transition-colors">
                {index + 1}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Theme Badge */}
                {/* <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {question.theme_name}
                  </span>
                  {question.is_mscr && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Nyckelfråga
                    </span>
                  )}
                </div> */}

                {/* Question Text */}
                <p className="text-lg text-gray-900 group-hover:text-brand-primary transition-colors">
                  {question.question_text}
                </p>

                
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* View All Themes Link */}
      <div className="text-center pt-4">
        <button
          onClick={() => navigate('/client')}
          className="text-brand-primary hover:text-brand-primary-dark font-medium underline"
        >
          Se översikt över alla livsteman
        </button>
      </div>
      </div>
    </div>
  );
};

export default QuestionCarousel;
